import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

export function Cursor() {
  const outer = useRef<HTMLDivElement>(null)
  const inner = useRef<HTMLDivElement>(null)
  const [bursts, setBursts] = useState<{ id: number; x: number; y: number }[]>([])
  const [isHovering, setIsHovering] = useState(false)

  const ox = useRef(0)
  const oy = useRef(0)
  const tx = useRef(0)
  const ty = useRef(0)
  const raf = useRef(0)

  useEffect(() => {
    // Check for touch device
    if (window.matchMedia('(pointer: coarse)').matches) {
      return
    }

    const move = (e: MouseEvent) => {
      tx.current = e.clientX
      ty.current = e.clientY
      if (inner.current) {
        inner.current.style.transform = `translate3d(${tx.current - 5}px, ${ty.current - 5}px, 0)`
      }

      // Check hover targets
      const el = document.elementFromPoint(tx.current, ty.current)
      const isHover = el?.closest('[data-cursor]')
      setIsHovering(!!isHover)
    }

    const tick = () => {
      ox.current += (tx.current - ox.current) * 0.08
      oy.current += (ty.current - oy.current) * 0.08
      if (outer.current) {
        outer.current.style.transform = `translate3d(${ox.current - (isHovering ? 30 : 18)}px, ${oy.current - (isHovering ? 30 : 18)}px, 0)`
      }
      raf.current = requestAnimationFrame(tick)
    }

    window.addEventListener('mousemove', move, { passive: true })
    raf.current = requestAnimationFrame(tick)

    return () => {
      window.removeEventListener('mousemove', move)
      cancelAnimationFrame(raf.current)
    }
  }, [isHovering])

  // Click burst
  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) {
      return
    }

    const click = (e: MouseEvent) => {
      const id = Date.now()
      setBursts((b) => [...b, { id, x: e.clientX, y: e.clientY }])
      setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 600)
    }

    window.addEventListener('click', click)
    return () => window.removeEventListener('click', click)
  }, [])

  // Hide on touch
  if (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches) {
    return null
  }

  return (
    <>
      {/* Outer ring */}
      <div
        ref={outer}
        className="cursor-outer"
        style={{
          position: 'fixed',
          width: isHovering ? 60 : 36,
          height: isHovering ? 60 : 36,
          border: '1.5px solid var(--lime)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 9999,
          transition: 'width 0.3s, height 0.3s, background 0.3s, opacity 0.3s',
          mixBlendMode: 'difference',
          background: isHovering ? 'var(--lime-dim)' : 'transparent'
        }}
      />

      {/* Inner dot */}
      <div
        ref={inner}
        className="cursor-inner"
        style={{
          position: 'fixed',
          width: 10,
          height: 10,
          background: 'var(--lime)',
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 9999,
          mixBlendMode: 'difference'
        }}
      />

      {/* Click bursts */}
      {bursts.map((b) => (
        <motion.div
          key={b.id}
          style={{
            position: 'fixed',
            left: b.x,
            top: b.y,
            pointerEvents: 'none',
            zIndex: 9998
          }}
          initial={{ scale: 0, opacity: 0.8 }}
          animate={{ scale: 3, opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '1px solid var(--lime)',
              transform: 'translate(-50%, -50%)'
            }}
          />
        </motion.div>
      ))}
    </>
  )
}
